import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FOOTBALL_POSITIONS, PROFESSIONS, formatCustomProfession } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Trophy, Compass, Users as UsersIcon, Heart, Globe, Briefcase, CalendarIcon } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "athlete", label: "Athlete", icon: Trophy, desc: "Play & be discovered" },
  { value: "scout", label: "Scout", icon: Compass, desc: "Find talent" },
  { value: "coach", label: "Coach", icon: UsersIcon, desc: "Build your squad" },
  { value: "explorer", label: "Explorer", icon: Globe, desc: "Join & browse the network" },
  { value: "professional", label: "Professional", icon: Briefcase, desc: "Physio, doctor, agent & more" },
  { value: "parent", label: "Parent", icon: Heart, desc: "Follow your child" },
];

const UEFA_LEVELS = ["UEFA C", "UEFA B", "UEFA A", "UEFA Pro", "UEFA Elite Youth A", "Other (specify)", "I'll add this later"];
const COACHING_ROLES = ["Head Coach", "Assistant Coach", "Goalkeeper Coach", "Fitness Coach", "Youth Coach", "Analyst", "Other"];
const SCOUT_FOCUS = ["Youth Players", "Senior Players", "Goalkeepers", "Defenders", "Midfielders", "Forwards", "General"];

function DateField({ value, onChange, placeholder = "Pick a date", testId }) {
  const [open, setOpen] = useState(false);
  const selectedDate = value ? new Date(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" type="button" data-testid={testId}
          className={`mt-1.5 w-full justify-start border-[#E6E6E6] font-normal ${value ? "text-zinc-900" : "text-zinc-400"}`}>
          <CalendarIcon size={14} className="mr-2 text-zinc-400" />
          {value ? new Date(value).toLocaleDateString() : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selectedDate}
          onSelect={(d) => { if (d) { onChange(d.toISOString().slice(0, 10)); setOpen(false); } }}
          captionLayout="dropdown-buttons" fromYear={1950} toYear={new Date().getFullYear()}
          defaultMonth={selectedDate || new Date(2008, 0)}
          classNames={{ caption_label: "hidden", caption_dropdowns: "flex gap-2 items-center justify-center",
            dropdown: "appearance-none bg-white border border-[#E6E6E6] rounded-md px-2 py-1 text-sm cursor-pointer hover:border-[#074572] focus:outline-none focus:border-[#074572]",
            vhidden: "sr-only" }} />
      </PopoverContent>
    </Popover>
  );
}

export default function ChooseRolePage() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [dob, setDob] = useState("");
  const [position, setPosition] = useState("");
  const [club, setClub] = useState("");
  const [coachingLicense, setCoachingLicense] = useState("");
  const [customLicense, setCustomLicense] = useState("");
  const [coachingRole, setCoachingRole] = useState("");
  const [scoutingRegion, setScoutingRegion] = useState("");
  const [scoutingFocus, setScoutingFocus] = useState("");
  const [profession, setProfession] = useState("");
  const [customProfession, setCustomProfession] = useState("");
  const [childName, setChildName] = useState("");
  const [childDob, setChildDob] = useState("");
  const [childClub, setChildClub] = useState("");

  if (loading) return null;
  if (!user) { navigate("/login", { replace: true }); return null; }
  if (user.role) { navigate("/feed", { replace: true }); return null; }

  const submit = async () => {
    setError("");
    if (!role) { setError("Please choose how you want to use SportsUnity."); return; }
    if (role === "athlete" && (!dob || !position || !club.trim())) { setError("Athletes must provide date of birth, position and academy/club."); return; }
    if (role === "coach" && coachingLicense === "Other (specify)" && !customLicense.trim()) { setError("Please enter your diploma/license, or choose \"I'll add this later\"."); return; }
    if (role === "scout" && (!club.trim() || !scoutingRegion.trim() || !scoutingFocus)) { setError("Scouts must provide organization, region and main focus."); return; }
    if (role === "parent" && (!childName.trim() || !childDob || !childClub.trim())) { setError("Parents must provide child name, date of birth and academy/club."); return; }
    if (role === "professional") {
      if (!profession) { setError("Please select your profession."); return; }
      if (profession === "Other" && !customProfession.trim()) { setError("Please enter your profession."); return; }
    }

    const payload = { role };
    if (role === "athlete") Object.assign(payload, { date_of_birth: dob, position, club });
    if (role === "coach") {
      let lic = coachingLicense;
      if (coachingLicense === "I'll add this later" || !coachingLicense) lic = "";
      else if (coachingLicense === "Other (specify)") lic = customLicense.trim();
      Object.assign(payload, { coaching_license: lic || undefined, club: club || undefined, coaching_role: coachingRole || undefined });
    }
    if (role === "scout") Object.assign(payload, { club, scouting_region: scoutingRegion, scouting_focus: scoutingFocus });
    if (role === "parent") Object.assign(payload, { child_name: childName, child_date_of_birth: childDob, child_club: childClub });
    if (role === "explorer" && club.trim()) Object.assign(payload, { club });
    if (role === "professional") Object.assign(payload, { profession: profession === "Other" ? customProfession.trim() : profession, club: club || undefined });

    setSubmitting(true);
    try {
      await api.post("/auth/complete-registration", payload);
      await checkAuth();
      toast.success("Welcome to SportsUnity!");
      navigate("/feed", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="choose-role-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-white border border-[#E6E6E6] flex items-center justify-center p-1">
            <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
          </div>
          <span className="text-lg font-semibold text-[#074572]" style={{ fontFamily: "Outfit" }}>SportsUnity</span>
        </div>

        <h1 className="text-2xl font-medium text-zinc-900 mb-1" style={{ fontFamily: "Outfit" }}>How will you use SportsUnity?</h1>
        <p className="text-sm text-zinc-500 mb-6">Pick the option that fits you best to finish setting up your account.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600" data-testid="choose-role-error">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-2 mb-4" data-testid="choose-role-grid">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const active = role === r.value;
            return (
              <button key={r.value} type="button" onClick={() => setRole(r.value)} data-testid={`choose-role-${r.value}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-left transition-all ${active ? "border-[#074572] bg-[#074572]/5 ring-1 ring-[#074572]/30" : "border-[#E6E6E6] hover:border-[#074572]/40 hover:bg-zinc-50"}`}>
                <Icon size={16} className={active ? "text-[#074572]" : "text-zinc-500"} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${active ? "text-[#074572]" : "text-zinc-900"}`}>{r.label}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{r.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {role === "athlete" && (
          <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="choose-athlete-fields">
            <div><Label className="text-zinc-700 text-sm">Date of Birth</Label><DateField value={dob} onChange={setDob} placeholder="Select your date of birth" testId="choose-dob" /></div>
            <div><Label className="text-zinc-700 text-sm">Position</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="choose-position-select"><SelectValue placeholder="Select position" /></SelectTrigger>
                <SelectContent>{FOOTBALL_POSITIONS.concat(["Other"]).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-zinc-700 text-sm">Academy / Club</Label><Input value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="choose-club-input" placeholder="e.g. PAOK U19" /></div>
          </div>
        )}

        {role === "coach" && (
          <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="choose-coach-fields">
            <div><Label className="text-zinc-700 text-sm">Diploma / License Level</Label>
              <Select value={coachingLicense} onValueChange={setCoachingLicense}>
                <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="choose-license-select"><SelectValue placeholder="Select license (or add later)" /></SelectTrigger>
                <SelectContent>{UEFA_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            {coachingLicense === "Other (specify)" && (
              <div><Label className="text-zinc-700 text-sm">Your diploma / license</Label>
                <Input value={customLicense} onChange={(e) => setCustomLicense(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="choose-license-other" placeholder="e.g. National C License" /></div>
            )}
            <div><Label className="text-zinc-700 text-sm">Current Team / Club (optional)</Label><Input value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="choose-coach-club" placeholder="e.g. Olympiacos FC" /></div>
            <div><Label className="text-zinc-700 text-sm">Coaching Role (optional)</Label>
              <Select value={coachingRole} onValueChange={setCoachingRole}>
                <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="choose-coaching-role-select"><SelectValue placeholder="Select coaching role" /></SelectTrigger>
                <SelectContent>{COACHING_ROLES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-[#E6E6E6] rounded p-2 leading-snug">You can add your diploma — including uploading the certificate for verification — later from Settings.</p>
          </div>
        )}

        {role === "scout" && (
          <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="choose-scout-fields">
            <div><Label className="text-zinc-700 text-sm">Organization / Club</Label><Input value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="choose-scout-club" placeholder="e.g. Chelsea FC, Independent" /></div>
            <div><Label className="text-zinc-700 text-sm">Scouting Region</Label><Input value={scoutingRegion} onChange={(e) => setScoutingRegion(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="choose-region-input" placeholder="e.g. Greece, Europe" /></div>
            <div><Label className="text-zinc-700 text-sm">Main Focus</Label>
              <Select value={scoutingFocus} onValueChange={setScoutingFocus}>
                <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="choose-focus-select"><SelectValue placeholder="Select focus" /></SelectTrigger>
                <SelectContent>{SCOUT_FOCUS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        )}

        {role === "professional" && (
          <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="choose-professional-fields">
            <div><Label className="text-zinc-700 text-sm">Profession</Label>
              <Select value={profession} onValueChange={setProfession}>
                <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="choose-profession-select"><SelectValue placeholder="Select profession" /></SelectTrigger>
                <SelectContent>{PROFESSIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            {profession === "Other" && (
              <div><Label className="text-zinc-700 text-sm">Your profession</Label>
                <Input value={customProfession} onChange={(e) => setCustomProfession(formatCustomProfession(e.target.value))} className="mt-1.5 border-[#E6E6E6]" data-testid="choose-profession-other" placeholder="e.g. Kinesiologist" /></div>
            )}
            <div><Label className="text-zinc-700 text-sm">Organization / Club (optional)</Label><Input value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="choose-professional-club" placeholder="e.g. AEK Athens" /></div>
          </div>
        )}

        {role === "explorer" && (
          <p className="text-[12px] text-zinc-500 bg-zinc-50 border border-[#E6E6E6] rounded p-3 leading-snug" data-testid="choose-explorer-note">
            Explorers can browse the network, discover talent and connect — the same access as scouts and coaches. You can add more details later in Settings.
          </p>
        )}

        <Button onClick={submit} disabled={submitting} className="w-full mt-5 bg-[#F85037] hover:bg-[#e04530] text-white font-medium h-10" data-testid="choose-role-submit">
          {submitting ? "Setting up…" : "Continue"}
        </Button>
      </div>
    </div>
  );
}
